// Component for regenerate model selection - compact design with smart positioning
import React from "react";
import { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
const RegenerateModelsDropdown = ({
  models = [],
  selectedModel = "",
  onSelect = () => {},
  isLoading = false,
  placeholder = "Select a model",
  forceOpen = false,
  position = "bottom", // 'top' | 'bottom' - smart positioning
  onClose = () => {},
  triggerRef = null, // Reference to the button that triggered the dropdown
}) => {
  const [isOpen, setIsOpen] = useState(forceOpen);
  const dropdownRef = useRef(null);

  // Filter models to show only active text models (exclude image models and inactive ones)
  const textModels = useMemo(() => {
    return models
      .filter(
        (model) =>
          model.active !== false &&
          model.modelType !== "image" &&
          model.name !== "Auto" &&
          model.label !== "Auto"
      )
      .map((model) => ({
        ...model,
        label: model.title || model.name || "Unknown Model",
        value: model._id,
        originalName: model.name,
      }));
  }, [models]);

  // Group models by provider for compact display - memoized for performance
  const { groups, providerDisplayNames } = useMemo(() => {
    const groups = {};
    const providerDisplayNames = {};
    textModels.forEach((model) => {
      let providerRaw = model.provider;
      if (
        !providerRaw ||
        typeof providerRaw !== "string" ||
        providerRaw.trim() === ""
      ) {
        providerRaw = "Other";
      }
      const providerKey = providerRaw.trim().toLowerCase();
      if (!groups[providerKey]) {
        groups[providerKey] = [];
        providerDisplayNames[providerKey] = providerRaw;
      }
      groups[providerKey].push({ ...model, _dropdownName: model.label });
    });
    return { groups, providerDisplayNames };
  }, [textModels]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (isOpen && dropdownRef.current) {
        // Check if click is outside the dropdown container
        if (!dropdownRef.current.contains(e.target)) {
          setIsOpen(false);
          onClose();
        }
      }
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [isOpen, onClose]);

  // If forceOpen changes, update isOpen accordingly
  useEffect(() => {
    if (forceOpen) setIsOpen(true);
  }, [forceOpen]);

  const handleSelect = (model, event) => {
    // Prevent event bubbling to avoid conflicts with outside click handler
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const modelId = model._id?.$oid || model._id || model.value || model.name;

    try {
      onSelect(modelId);
    } catch (error) {
      error("RegenerateModelsDropdown: Error calling onSelect:", error);
    }

    // Close dropdown immediately
    setIsOpen(false);
    onClose();
  };

  // Find selected model
  const selected =
    textModels.find(
      (m) =>
        m._id?.$oid === selectedModel ||
        m.value === selectedModel ||
        m.name === selectedModel
    ) || textModels[0];

  return (
    <div className="regenerate-models-dropdown relative" ref={dropdownRef}>
      {!forceOpen && (
        <button
          type="button"
          className="regenerate-models-dropdown-toggle flex items-center justify-between px-3 py-1.5 text-xs font-medium bg-bg-50 text-fg-50 hover:bg-bg-40 rounded-lg transition-all duration-200 border border-bd-50"
          onClick={() => setIsOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          disabled={isLoading}
          title="Select Model for Regeneration"
        >
          <span className="flex items-center gap-2 truncate">
            <span>{selected?.label || selected?.name || placeholder}</span>
          </span>
          <ChevronDown
            size={12}
            className={`text-fg-60 transition-transform duration-200 ml-2 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>
      )}

      {isOpen && (
        <div
          className="absolute z-[9999] mt-1"
          style={{
            minWidth: "280px",
            maxWidth: "320px",
            top: position === "top" ? "auto" : "100%",
            bottom: position === "top" ? "100%" : "auto",
            left: "0",
            zIndex: 99999,
            position: "absolute",
            pointerEvents: "auto",
          }}
        >
          <div
            className="regenerate-models-dropdown-list bg-white dark:bg-bg-60 rounded-xl shadow-2xl border border-bd-50 overflow-hidden flex flex-col backdrop-blur-sm"
            style={{
              maxHeight: "300px",
              opacity: 1,
              zIndex: 99999,
              position: "relative",
              pointerEvents: "auto",
              transform: "translateZ(0)", // Force hardware acceleration
            }}
          >
            {/* Header */}
            <div className="px-3 py-2 border-b border-bd-50 bg-bg-50/30">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-fg-50">
                  Select Model
                </span>
                {isLoading && (
                  <Loader2 className="w-3 h-3 animate-spin text-fg-60" />
                )}
              </div>
            </div>

            {/* Content */}
            <div className="overflow-y-auto flex-1">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-4 h-4 animate-spin text-fg-60" />
                  <span className="ml-2 text-xs text-fg-60">
                    Loading models...
                  </span>
                </div>
              ) : Object.keys(groups).length === 0 ? (
                <div className="flex items-center justify-center py-8 text-xs text-fg-60">
                  No models available
                </div>
              ) : (
                <div className="space-y-1 p-2">
                  {/* Provider Groups */}
                  {Object.entries(groups).map(
                    ([providerKey, providerModels]) => (
                      <div key={providerKey} className="space-y-1">
                        {/* Provider Header - Compact */}
                        <div className="px-2 py-0.5">
                          <h4 className="text-[10px] font-medium text-fg-60 uppercase tracking-wide">
                            {providerDisplayNames[providerKey]}
                          </h4>
                        </div>

                        {/* Models List - Compact with provider on right */}
                        {providerModels.map((model, idx) => {
                          return (
                            <button
                              key={`${providerKey}-${
                                model._id || model.value || model.name
                              }-${idx}`}
                              type="button"
                              className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-left transition-colors group hover:bg-bg-50 text-fg-50"
                              onClick={(e) => handleSelect(model, e)}
                            >
                              {/* Model name on left */}
                              <div className="flex-1 min-w-0">
                                <span className="text-xs font-medium truncate block">
                                  {model.label}
                                </span>
                              </div>

                              {/* Provider on right */}
                              <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                                <span className="text-[10px] text-fg-60 font-normal">
                                  {providerDisplayNames[providerKey]}
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegenerateModelsDropdown;
